

















































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { firebase } from "@/config/firebase";
import { appVentas, usuarioVentas } from "@/config/firebaseVentas";
import { periodoAnterior } from "@/helpers/facturacion";
import { Contrato } from "@/typings/store/plugins/easyFirestore/contratos";
import {
  ContratoVentas,
  EstadoVentaMes,
  EventoVenta,
  ResumenVentaMes,
  rangoDelMes,
  resumenSinRegistro,
  resumirVentasMes,
  sugerirVinculo,
  ventaFinal,
} from "@/helpers/ventasSistema";

interface VentaCargada {
  ventaNeta: number;
  fuente: string;
  ajuste?: number;
  motivoAjuste?: string;
}

interface FilaVenta {
  idContrato: string;
  codigo: number;
  nombre: string;
  local: string;
  tieneVariable: boolean;
  facturado: boolean;
  codigoVentas: string | null;
  codigoVentasGuardado: string | null;
  motivo: string;
  resumen: ResumenVentaMes | null;
  ajuste: number;
  motivoAjuste: string;
  cargada: VentaCargada | null;
}

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const FUENTE = "Sistema de ventas";
const MAX_LOTE = 400;

@Component
export default class VentasSistema extends Vue {
  @Prop({ required: true }) readonly periodoFacturacion!: string;
  /** Carga sola las ventas completas ya vinculadas y avisa el resultado con el evento «automatico». */
  @Prop({ type: Boolean, default: false }) readonly automatico!: boolean;

  usuario: firebase.User | null = null;
  login = { correo: "", clave: "" };
  conectando = false;
  cargando = false;
  guardando = false;
  error = "";
  resultado = "";
  soloVariable = true;
  filas: FilaVenta[] = [];
  seleccionadas: FilaVenta[] = [];
  contratosVentas: ContratoVentas[] = [];
  resumenMes = new Map<string, ResumenVentaMes>();

  headers = [
    { text: "Contrato", value: "nombre" },
    { text: "Sistema de ventas", value: "codigoVentas", sortable: false, width: 260 },
    { text: "Registro del mes", value: "estado", sortable: false },
    { text: "Venta sistema", value: "total", align: "end" },
    { text: "Ajuste", value: "ajuste", sortable: false, width: 170 },
    { text: "Venta a facturar", value: "final", align: "end", sortable: false },
    { text: "Cargada hoy", value: "cargada", sortable: false },
  ];

  colorEstado: Record<EstadoVentaMes, string> = { completo: "success", "con-huecos": "info", incompleto: "warning", "sin-registro": "error" };
  textoEstado: Record<EstadoVentaMes, string> = { completo: "completo", "con-huecos": "con huecos", incompleto: "incompleto", "sin-registro": "sin registro" };

  get db(): firebase.firestore.Firestore {
    return firebase.firestore();
  }

  get periodoVenta(): string {
    return periodoAnterior(this.periodoFacturacion);
  }

  get filasVisibles(): FilaVenta[] {
    return this.soloVariable ? this.filas.filter((f) => f.tieneVariable) : this.filas;
  }

  get opcionesVentas(): Array<{ text: string; value: string }> {
    return this.contratosVentas
      .map((c) => ({ text: `${c.code} · ${c.tradename || c.businessname} · ${c.shop || ""}`, value: String(c.code) }))
      .sort((a, b) => Number(a.value) - Number(b.value));
  }

  get conteo(): Record<EstadoVentaMes, number> {
    const c: Record<EstadoVentaMes, number> = { completo: 0, "con-huecos": 0, incompleto: 0, "sin-registro": 0 };
    this.filasVisibles.forEach((f) => f.resumen && c[f.resumen.estado]++);
    return c;
  }

  get sinVinculo(): number {
    return this.filasVisibles.filter((f) => !f.codigoVentas).length;
  }

  get seleccionIncompleta(): number {
    return this.seleccionadas.filter((f) => f.resumen && ["incompleto", "sin-registro"].includes(f.resumen.estado)).length;
  }

  nombreMes(periodo: string): string {
    const [y, m] = periodo.split("-").map(Number);
    return `${MESES[m - 1]} ${y}`;
  }

  pesos(v: number): string {
    return Number(v || 0).toCurrency({ symbol: "$" });
  }

  final(f: FilaVenta): number {
    return ventaFinal(f.resumen?.total ?? 0, f.ajuste);
  }

  /** [3,4,5,9] → "3–5, 9" */
  resumirDias(dias: number[]): string {
    const tramos: string[] = [];
    for (let i = 0; i < dias.length; i++) {
      let j = i;
      while (j + 1 < dias.length && dias[j + 1] === dias[j] + 1) j++;
      tramos.push(i === j ? `${dias[i]}` : `${dias[i]}–${dias[j]}`);
      i = j;
    }
    return tramos.length > 4 ? `${tramos.slice(0, 4).join(", ")}…` : tramos.join(", ");
  }

  seleccionable(f: FilaVenta): boolean {
    return !f.facturado && !!f.codigoVentas && !!f.resumen;
  }

  /** Se marcan solas las que conviene cargar: meses que llegan al último día y cambian lo cargado. */
  recomendada(f: FilaVenta): boolean {
    if (!this.seleccionable(f) || !f.resumen) return false;
    if (!["completo", "con-huecos"].includes(f.resumen.estado)) return false;
    return !f.cargada || Math.abs(f.cargada.ventaNeta - this.final(f)) > 2 || f.cargada.fuente !== FUENTE;
  }

  async created(): Promise<void> {
    this.usuario = await usuarioVentas();
    if (this.usuario) await this.cargar();
    else if (this.automatico) this.$emit("automatico", null);
  }

  @Watch("periodoFacturacion")
  async onPeriodo(): Promise<void> {
    if (this.usuario) await this.cargar();
  }

  async conectar(): Promise<void> {
    this.conectando = true;
    this.error = "";
    try {
      const cred = await appVentas().auth().signInWithEmailAndPassword(this.login.correo.trim(), this.login.clave);
      this.usuario = cred.user;
      this.login.clave = "";
      await this.cargar();
    } catch (e) {
      this.error = "No se pudo iniciar sesión en el sistema de ventas. Revisa el correo y la contraseña.";
    } finally {
      this.conectando = false;
    }
  }

  async desconectar(): Promise<void> {
    await appVentas().auth().signOut();
    this.usuario = null;
    this.filas = [];
    this.seleccionadas = [];
  }

  async eventosDelMes(periodo: string): Promise<EventoVenta[]> {
    const [desde, hasta] = rangoDelMes(periodo);
    const snap = await appVentas().firestore().collection("eventos").where("start", ">=", desde).where("start", "<=", hasta).get();
    return snap.docs.map((d) => d.data() as EventoVenta);
  }

  async cargar(): Promise<void> {
    this.cargando = true;
    this.error = "";
    this.resultado = "";
    try {
      const periodo = this.periodoVenta;
      const [snapContratos, snapDivisiones, snapVentas, snapFacturas, snapCV, eventos] = await Promise.all([
        this.db.collection("dev/v1/contratos").where("estadoContrato", "==", "Vigente").get(),
        this.db.collection("dev/v1/condicionesDivisiones").get(),
        this.db.collection("dev/v1/ventasLocatarios").get(),
        this.db.collection("dev/v1/facturas").where("periodoFacturado", "==", this.periodoFacturacion).get(),
        appVentas().firestore().collection("contratos").get(),
        this.eventosDelMes(periodo),
      ]);
      this.contratosVentas = snapCV.docs.map((d) => d.data() as ContratoVentas);
      const resumen = resumirVentasMes(eventos, periodo);
      this.resumenMes = resumen;

      const conVariable = new Set(
        snapDivisiones.docs
          .map((d) => d.data())
          .filter((d) => String(d.nombre_division).toUpperCase() === "ALQUILER VARIABLE")
          .map((d) => String(d.codigo_contrato))
      );
      const facturados = new Set(
        snapFacturas.docs.map((d) => d.data()).filter((f) => f.estado !== "Anulada").map((f) => String(f.idContrato))
      );
      // Ventas ya cargadas por contrato: la del mes y las anteriores, que sirven para sugerir el vínculo.
      const ventasPorContrato: Record<string, Record<string, number>> = {};
      const cargadas: Record<string, VentaCargada> = {};
      snapVentas.docs.forEach((d) => {
        const v = d.data();
        const k = String(v.codigoContrato);
        (ventasPorContrato[k] = ventasPorContrato[k] || {})[v.periodo] = Number(v.ventaNeta) || 0;
        if (v.periodo === periodo) cargadas[k] = v as VentaCargada;
      });

      // Totales del sistema para sugerir vínculos: este mes y, si hace falta, los meses ya cargados.
      const contratos = snapContratos.docs.map((d) => ({ ...(d.data() as Contrato), id: d.id }));
      const totales: Record<string, Record<string, number>> = {};
      resumen.forEach((r, code) => (totales[code] = { [periodo]: r.total }));
      if (contratos.some((c) => !c.codigoVentas)) {
        const anteriores = Array.from(new Set(Object.values(ventasPorContrato).flatMap((v) => Object.keys(v))))
          .filter((p) => p < periodo)
          .sort()
          .slice(-2);
        for (const p of anteriores)
          resumirVentasMes(await this.eventosDelMes(p), p).forEach((r, code) => ((totales[code] = totales[code] || {})[p] = r.total));
      }

      // Sugerencias en dos pasadas: primero las ventas iguales (seguras) y después RUT/local,
      // para que un RUT compartido no le quite el vínculo a quien ya cuadra por ventas.
      const usados = new Set(contratos.filter((c) => c.codigoVentas).map((c) => String(c.codigoVentas)));
      const sugeridas = new Map<string, { codigoVentas: string; motivo: string }>();
      for (const criterio of ["ventas", "datos"] as const)
        for (const c of contratos) {
          const codigo = String(c.codigo);
          if (c.codigoVentas || sugeridas.has(codigo)) continue;
          const s = sugerirVinculo(
            { codigo, rut: c.clienteSnapshot?.rut || "", local: c.localSnapshot?.codigo || "", ventas: ventasPorContrato[codigo] || {} },
            this.contratosVentas,
            totales,
            usados,
            criterio
          );
          if (s) {
            sugeridas.set(codigo, s);
            usados.add(s.codigoVentas);
          }
        }
      this.filas = contratos
        .map((c) => {
          const codigo = String(c.codigo);
          const guardado = c.codigoVentas ? String(c.codigoVentas) : null;
          const sugerida = sugeridas.get(codigo);
          const codigoVentas = guardado || sugerida?.codigoVentas || null;
          const motivo = guardado ? "" : sugerida?.motivo || "";
          const cargada = cargadas[codigo] || null;
          return {
            idContrato: c.id,
            codigo: c.codigo,
            nombre: c.clienteSnapshot?.nombre_comercial || c.clienteSnapshot?.nombre || codigo,
            local: c.localSnapshot?.codigo || "",
            tieneVariable: conVariable.has(codigo),
            facturado: facturados.has(c.id),
            codigoVentas,
            codigoVentasGuardado: guardado,
            motivo,
            resumen: codigoVentas ? resumen.get(codigoVentas) || resumenSinRegistro(codigoVentas, periodo) : null,
            // Un ajuste manual se conserva al volver a traer las ventas.
            ajuste: cargada && cargada.fuente === FUENTE ? Number(cargada.ajuste) || 0 : 0,
            motivoAjuste: cargada && cargada.fuente === FUENTE ? cargada.motivoAjuste || "" : "",
            cargada,
          } as FilaVenta;
        })
        .sort((a, b) => Number(b.tieneVariable) - Number(a.tieneVariable) || a.nombre.localeCompare(b.nombre));
      this.seleccionadas = this.filas.filter((f) => this.recomendada(f));

      if (this.automatico) {
        // Solo lo seguro: vínculo confirmado, mes completo y sin reemplazar lo que vino de otra fuente.
        const auto = this.filas.filter(
          (f) => f.tieneVariable && f.codigoVentasGuardado && this.recomendada(f) && (!f.cargada || f.cargada.fuente === FUENTE)
        );
        if (auto.length) await this.guardar(auto);
        this.$emit("automatico", { cargadas: auto.length, ...this.conteo, sinVinculo: this.sinVinculo, periodo: this.nombreMes(periodo) });
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Error al traer las ventas:", e);
      this.error = "No se pudieron traer las ventas del sistema de ventas. Revisa la conexión o vuelve a iniciar sesión.";
      if (this.automatico) this.$emit("automatico", null);
    } finally {
      this.cargando = false;
    }
  }

  cambiarVinculo(f: FilaVenta): void {
    f.motivo = "";
    f.resumen = f.codigoVentas
      ? this.resumenMes.get(f.codigoVentas) || resumenSinRegistro(f.codigoVentas, this.periodoVenta)
      : null;
    const resto = this.seleccionadas.filter((s) => s.codigo !== f.codigo);
    this.seleccionadas = this.recomendada(f) ? [...resto, f] : resto;
  }

  async guardar(filas: FilaVenta[]): Promise<void> {
    const sinMotivo = filas.find((f) => f.ajuste && !String(f.motivoAjuste || "").trim());
    if (sinMotivo) {
      this.error = `Indica el motivo del ajuste de ${sinMotivo.nombre}.`;
      return;
    }
    this.guardando = true;
    this.error = "";
    try {
      const periodo = this.periodoVenta;
      const ops: Array<(b: firebase.firestore.WriteBatch) => void> = [];
      for (const f of filas) {
        if (!this.seleccionable(f) || !f.resumen) continue;
        const r = f.resumen;
        const ajuste = Number(f.ajuste) || 0;
        const doc = {
          codigoContrato: f.codigo,
          periodo,
          ventaNeta: ventaFinal(r.total, ajuste),
          ventaSistema: r.total,
          ajuste,
          motivoAjuste: ajuste ? String(f.motivoAjuste).trim() : "",
          codigoVentas: f.codigoVentas,
          diasRegistrados: r.diasRegistrados,
          diasMes: r.diasMes,
          diasFaltantes: r.faltantes,
          estadoRegistro: r.estado,
          fuente: FUENTE,
          actualizado: new Date().toISOString(),
        };
        // Mismo id que la venta importada del Excel: la reemplaza en vez de sumarse.
        ops.push((b) => b.set(this.db.collection("dev/v1/ventasLocatarios").doc(`${f.codigo}-${periodo}`), doc));
        if (f.codigoVentas !== f.codigoVentasGuardado)
          ops.push((b) => b.update(this.db.collection("dev/v1/contratos").doc(f.idContrato), { codigoVentas: f.codigoVentas }));
      }
      for (let i = 0; i < ops.length; i += MAX_LOTE) {
        const lote = this.db.batch();
        ops.slice(i, i + MAX_LOTE).forEach((op) => op(lote));
        await lote.commit();
      }
      const n = filas.filter((f) => this.seleccionable(f)).length;
      if (!this.automatico) await this.cargar();
      this.resultado = `Se cargaron ${n} ventas de ${this.nombreMes(periodo)}.`;
      this.$emit("guardado", n);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Error al guardar las ventas:", e);
      this.error = "No se pudieron guardar las ventas. Vuelve a traerlas y revisa antes de reintentar.";
    } finally {
      this.guardando = false;
    }
  }
}
